<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const {
  orientation,
  status,
  createdAt,
  content,
  attachments,
  contentAttributes,
} = useMessageContext();

const { t } = useI18n();

const deliveryState = computed(
  () =>
    contentAttributes.value?.gowaDelivery?.state ||
    contentAttributes.value?.gowa_delivery?.state
);
const isPending = computed(() => deliveryState.value === 'pending');
const isUnknown = computed(() => deliveryState.value === 'unknown');
const deliveryLabel = computed(() => {
  if (isPending.value) return t('GOWA_MESSAGE_DELIVERY.PENDING');
  if (isUnknown.value) return t('GOWA_MESSAGE_DELIVERY.UNKNOWN');
  return t('CHAT_LIST.FAILED_TO_SEND');
});
const deliveryError = computed(() => {
  const errors = {
    pending: t('GOWA_MESSAGE_DELIVERY.ERRORS.PENDING'),
    unknown: t('GOWA_MESSAGE_DELIVERY.ERRORS.UNKNOWN'),
    not_sent: t('GOWA_MESSAGE_DELIVERY.ERRORS.NOT_SENT'),
    conflict: t('GOWA_MESSAGE_DELIVERY.ERRORS.CONFLICT'),
  };
  return errors[deliveryState.value];
});

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  if (isPending.value || deliveryState.value === 'conflict') return false;
  return (
    (deliveryState.value || !hasOneDayPassed(createdAt.value)) &&
    (hasContent || hasAttachments)
  );
});
</script>

<template>
  <div
    class="text-xs flex items-center gap-1.5"
    :class="isPending || isUnknown ? 'text-n-amber-11' : 'text-n-ruby-11'"
  >
    <span>{{ deliveryLabel }}</span>
    <div class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          :icon="isPending ? 'i-lucide-clock' : 'i-lucide-alert-triangle'"
          class="size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-52 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-all"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ deliveryError || error }}
      </div>
    </div>
    <button
      v-if="canRetry"
      v-tooltip="
        isUnknown
          ? t('GOWA_MESSAGE_DELIVERY.VERIFY')
          : t('GOWA_MESSAGE_DELIVERY.RETRY')
      "
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      :aria-label="
        isUnknown
          ? t('GOWA_MESSAGE_DELIVERY.VERIFY')
          : t('GOWA_MESSAGE_DELIVERY.RETRY')
      "
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon
        :icon="isUnknown ? 'i-lucide-search-check' : 'i-lucide-refresh-ccw'"
        class="size-[14px]"
      />
    </button>
  </div>
</template>
